import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  Input,
  input,
  OnInit,
  Output,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { ComponentType, InputType, PromptComponent, PromptInput } from '@k12/models';
import { toBool } from '@k12/utils';

@Component({

  selector: 'lib-pc-radio',
  imports: [CommonModule, MatButtonModule, MatRadioModule, ReactiveFormsModule],
  templateUrl: './pc-radio.component.html',
  styleUrl: './pc-radio.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PcRadioComponent implements OnInit {

  @Input() allowPointerEvents: boolean = false;
  @Input() configJson: PromptComponent = {
    componentType: ComponentType.RadioFormField,
    header: '',
    inputType: InputType.Radio,
    instructions: '',
    label: '',
    order: 0,
    inputs: [1, 2].map((n) => ({
      dataType: 'boolean',
      inputType: InputType.Radio,
      instructions: '',
      isRequired: true,
      label: 'Add descriptive text for this radio button.',
      order: n - 1,
      options: [],
      link: {}
    }))
  };
  @Input() formGroup: FormGroup = new FormGroup({});

  // Output names intentionally match PcCheckboxComponent (and every other pc-* form
  // component: pc-file-upload, pc-binary-decision, pc-select-sub-select,
  // pc-terms-and-conditions, pc-repeater-group-form, pc-form-field) rather than
  // introducing radio-specific names, so this component slots into the existing
  // generic PromptRendererComponent wiring without any downstream renames.
  @Output() cbConfig = new EventEmitter();
  @Output() checkboxSelect = new EventEmitter();
  @Output() openDrawer = new EventEmitter();

  public ComponentType: typeof ComponentType = ComponentType;
  public displayAdminFormField: WritableSignal<boolean> = signal(false);
  public editFormFieldInput = input<boolean>();
  public enableAddTextInputButton = input<boolean>();
  public enrollmentBuilderView = input<boolean>();
  public isEditDrawerOpen: WritableSignal<boolean> = signal(false);
  public showAddRadioButton: WritableSignal<boolean> = signal(false);

  private localTrackIdSeq = 0;
  private readonly formValuesVersion: WritableSignal<number> = signal(0);

  // Derives the currently-selected option purely from form state (whichever sibling
  // control is true), so mat-radio-group reflects a value restored from a saved draft
  // without needing its own bound control.
  public selectedControlName: Signal<string | undefined> = computed(() => {
    this.formValuesVersion();
    const selected = this.configJson.inputs.find((i) => this.formGroup?.get(this.controlName(i))?.value === true);
    return selected ? this.controlName(selected) : undefined;
  });

  // A radio group is mandatory when any option is flagged required. The edit drawer keeps every
  // option's isRequired in sync (all-on / all-off), so this behaves as a single reliable toggle in
  // both the admin builder and the end-user enrollment view.
  public get isMandatory(): boolean {
    return this.configJson.inputs?.some((input) => input.isRequired) ?? false;
  }

  constructor() {
    effect(() => {
      if (this.editFormFieldInput()) {
        this.showAddRadioButton.set(true);
      }
    });
  }

  public ngOnInit(): void {
    if (this.enrollmentBuilderView() && this.configJson.inputs?.length !== 0) {
      this.displayAdminFormField.set(true);
    }
    this.cbConfig.emit(this.configJson);
    if (!this.formGroup) {
      this.formGroup = new FormGroup({});
    }
    // One independent boolean control per option -- same shape as PcCheckboxComponent.
    // Mutual exclusion is enforced at click/emit time (see radioClick), not by the
    // form-group structure.
    this.configJson.inputs.forEach((item) => {
      this.assignLocalTrackId(item);
      const name = this.controlName(item);
      if (!this.formGroup.contains(name)) {
        this.formGroup.addControl(name, new FormControl(false));
      }
    });
    // Mirror PcCheckboxComponent: draft restore stores booleans as strings in
    // promptDataDictionary; coerce so selectedControlName / validators see real booleans.
    // Todo: move into returnFormInputValue when we can (same as checkbox).
    this.normalizeFormBooleans(this.formGroup);
    this.formGroup.valueChanges.subscribe(() => this.formValuesVersion.update((v) => v + 1));
  }

  // Saved inputs are keyed by their server-assigned GUID; unsaved (admin builder) inputs use a
  // transient local control name that is never sent to the server.
  public controlName(input: PromptInput): string {
    return input.id ?? input.localTrackId ?? `local_${input.order ?? 0}`;
  }

  public addInputField(): void {
    const newInput = {
      inputType: InputType.Radio,
      instructions: '',
      order: this.configJson.inputs.length,
      label: 'Add descriptive text for this radio button.',
      dataType: 'boolean',
      isRequired: this.isMandatory,
      link: {},
      options: [],
      localTrackId: this.nextLocalTrackId()
    };
    this.configJson = {
      ...this.configJson,
      inputs: [...this.configJson.inputs, newInput]
    };
    this.rebuildControls();
    this.cbConfig.emit(this.configJson);
  }

  // Mutual exclusion happens here: emit a control dict covering every sibling in the
  // group -- the clicked option true, everything else false -- reusing the exact
  // { formName, control } envelope PcCheckboxComponent already emits (just N=1 set
  // instead of a single independent toggle), so downstream handling stays generic.
  public radioClick(selectedName: string): void {
    const control: Record<string, boolean> = {};
    this.configJson.inputs.forEach((input) => {
      const name = this.controlName(input);
      control[name] = name === selectedName;
      this.formGroup.get(name)?.setValue(name === selectedName, { emitEvent: false });
    });
    this.formValuesVersion.update((v) => v + 1);
    this.checkboxSelect.emit({
      formName: this.configJson.id ?? '',
      control
    });
  }

  public openEditDrawer(index: number): void {
    this.isEditDrawerOpen.set(true);
    this.openDrawer.emit({ openDrawer: true, editIndex: index });
  }

  public removeField(index: number): void {
    const updatedInputs = this.configJson.inputs
      .filter((_, i) => i !== index)
      .map((item, i) => ({
        ...item,
        order: i
      }));

    this.configJson = {
      ...this.configJson,
      inputs: updatedInputs
    };

    this.rebuildControls();
    this.openDrawer.emit({ openDrawer: false });
    this.cbConfig.emit(this.configJson);
  }

  // Add/remove only happen in the admin builder where the control values are throwaway, so
  // the group can simply be rebuilt to match the current inputs.
  private rebuildControls(): void {
    if (!this.formGroup) return;
    Object.keys(this.formGroup.controls).forEach((name) => this.formGroup.removeControl(name, { emitEvent: false }));
    this.configJson.inputs.forEach((input) => {
      this.formGroup.addControl(this.controlName(input), new FormControl(false), { emitEvent: false });
    });
  }

  private normalizeFormBooleans(form: FormGroup): void {
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      if (!control) return;
      const val = control.value;
      // if it's string (or empty) convert to boolean
      if (typeof val === 'string' || val === '' || typeof val === 'number') {
        control.setValue(toBool(val), { emitEvent: false });
      }
    });
  }

  // Gives unsaved inputs (no server-assigned id) a stable identity, set once at creation, so
  // the template can track them by plain property access instead of calling a method on every
  // change-detection pass. Stripped before the config is persisted -- see PromptInput.localTrackId.
  private assignLocalTrackId(input: PromptInput): void {
    if (!input.id && !input.localTrackId) {
      input.localTrackId = this.nextLocalTrackId();
    }
  }

  private nextLocalTrackId(): string {
    return `local_${this.localTrackIdSeq++}`;
  }
}
